import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import type { ConnectedApp } from "shared/AgentAccess";
import { deleteAccount, getAccountExport } from "src/CalmLensApi";
import ConnectedAppsPanel from "src/components/app/ConnectedAppsPanel/ConnectedAppsPanel";
import LanguagePanel from "src/components/app/LanguagePanel/LanguagePanel";
import PortalPage from "src/components/app/PortalPage/PortalPage";
import SubscriptionSettings from "src/components/app/SubscriptionSettingsPanel/SubscriptionSettings";
import Button from "src/components/common/Button/Button";
import Callout from "src/components/common/Callout/Callout";
import ConfirmationDialog from "src/components/common/ConfirmationDialog/ConfirmationDialog";
import ContentPanel from "src/components/common/ContentPanel/ContentPanel";
import { errorToast } from "src/components/common/Toasts/ToastActions";
import Typography from "src/components/common/Typography/Typography";
import { useAuth } from "src/lib/contexts/AuthContext";
import { DeferredLoaderData } from "src/lib/LoaderData";
import { raceLoaderData } from "src/lib/LoaderResult";
import { useSearchParams } from "src/lib/Navigation";
import { getProfilePageData } from "src/server/functions/Portal";
import { useExtracted } from "#i18n";

export const Route = createFileRoute("/_authed/portal/profile")({
  loader: () => raceLoaderData(getProfilePageData()),
  component: ProfileRoute,
});

function ProfileRoute() {
  return (
    <DeferredLoaderData result={Route.useLoaderData()}>
      {(data) => <ProfilePage initialConnectedApps={data?.initialConnectedApps} />}
    </DeferredLoaderData>
  );
}

function ProfilePage({ initialConnectedApps }: { initialConnectedApps?: ConnectedApp[] }) {
  const translateMessage = useExtracted();
  const searchParams = useSearchParams();
  const billingResult = searchParams.get("billing");
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { authState } = useAuth();
  const user = authState?.user;
  const displayName =
    user?.firstName && user?.lastName
      ? translateMessage("{firstName} {lastName}", {
          firstName: user.firstName,
          lastName: user.lastName,
        })
      : user?.email || translateMessage("Profile");

  return (
    <PortalPage>
      <PortalPage.Header>
        <PortalPage.Heading>
          <PortalPage.Title>{translateMessage("Profile")}</PortalPage.Title>
          <PortalPage.Description>
            {translateMessage("Manage your account and billing")}
          </PortalPage.Description>
        </PortalPage.Heading>
      </PortalPage.Header>
      <PortalPage.Content>
        {billingResult === "success" && (
          <Callout role="status" color="success" size="small">
            {translateMessage(
              "Billing is set up. Your plan appears here within a minute; refresh to see it.",
            )}
          </Callout>
        )}
        {billingResult === "cancelled" && (
          <Callout role="status" color="info" size="small">
            {translateMessage("Billing setup cancelled. You weren’t charged.")}
          </Callout>
        )}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          <ContentPanel>
            <ContentPanel.Header>
              <ContentPanel.Heading>
                <ContentPanel.Title>{translateMessage("Account")}</ContentPanel.Title>
                <ContentPanel.Description>
                  {translateMessage("Your name and email address")}
                </ContentPanel.Description>
              </ContentPanel.Heading>
            </ContentPanel.Header>
            <ContentPanel.Content>
              <div className="grid min-w-0 gap-2 wrap-anywhere">
                <div className="font-medium">{displayName}</div>
                {user?.email ? <div className="text-muted">{user.email}</div> : null}
              </div>
            </ContentPanel.Content>
          </ContentPanel>

          <ContentPanel>
            <ContentPanel.Header>
              <ContentPanel.Heading>
                <ContentPanel.Title>{translateMessage("Billing")}</ContentPanel.Title>
                <ContentPanel.Description>
                  {translateMessage("Payment details and invoices")}
                </ContentPanel.Description>
              </ContentPanel.Heading>
            </ContentPanel.Header>
            <ContentPanel.Content>
              <SubscriptionSettings />
            </ContentPanel.Content>
          </ContentPanel>

          <LanguagePanel />

          <ConnectedAppsPanel initialApps={initialConnectedApps} />

          <ContentPanel className="lg:col-span-2">
            <ContentPanel.Header>
              <ContentPanel.Heading>
                <ContentPanel.Title>{translateMessage("Your data")}</ContentPanel.Title>
              </ContentPanel.Heading>
            </ContentPanel.Header>
            <ContentPanel.Content>
              <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                <div>
                  <Typography weight="medium">{translateMessage("Download your data")}</Typography>
                  <Typography variant="bodySmall" tone="muted">
                    {translateMessage("Export your account and project data as JSON.")}
                  </Typography>
                </div>
                <Button
                  label={translateMessage("Download data")}
                  variant="outline"
                  loading={exporting}
                  onClick={async () => {
                    setExporting(true);
                    try {
                      const { data } = await getAccountExport.submit({});
                      const url = URL.createObjectURL(
                        new Blob([data], { type: "application/json" }),
                      );
                      const link = document.createElement("a");
                      link.href = url;
                      link.download = `calmlens-account-export-${Date.now()}.json`;
                      link.click();
                      URL.revokeObjectURL(url);
                    } catch (error) {
                      errorToast(translateMessage("Failed to download your data"), {
                        error: error instanceof Error ? error : undefined,
                      });
                    } finally {
                      setExporting(false);
                    }
                  }}
                />
              </div>
              <div className="flex flex-col items-start justify-between gap-4 border-t pt-6 sm:flex-row sm:items-center">
                <div>
                  <Typography weight="medium" tone="danger">
                    {translateMessage("Delete account")}
                  </Typography>
                  <Typography variant="bodySmall" tone="muted">
                    {translateMessage("Permanently delete your account and all associated data.")}
                  </Typography>
                </div>
                <Button
                  label={translateMessage("Delete account")}
                  color="danger"
                  onClick={() => setDeleting(true)}
                />
              </div>
            </ContentPanel.Content>
          </ContentPanel>
        </div>
        <ConfirmationDialog
          visible={deleting}
          title={translateMessage("Delete account")}
          description={translateMessage(
            "Delete your account and all data? This action cannot be undone.",
          )}
          confirmLabel={translateMessage("Delete account")}
          confirmationText={user?.email ?? displayName}
          errorMessage={translateMessage("Couldn’t delete your account. Try again.")}
          onClose={() => setDeleting(false)}
          onConfirm={async () => {
            await deleteAccount.submit({ body: { confirmation: "DELETE" } });
            window.location.replace("/");
          }}
        />
      </PortalPage.Content>
    </PortalPage>
  );
}
